import { CloseIcon, IconButton } from '@ds/coolshop';
import { getTimeoutWarningForMessage } from '@src/_new/modules/processing-timeout';
import {
    SnackbarAction,
    SnackbarKey,
    useSnackbar,
    VariantType,
    WithSnackbarProps,
} from 'notistack';
import React from 'react';

let useSnackbarRef: WithSnackbarProps;
export const SnackbarUtilsConfigurator: React.FC = () => {
    useSnackbarRef = useSnackbar();
    return null;
};

type ToastOptions = {
    replaceKey?: SnackbarKey;
    persist?: boolean;
};

/**
 * Wrapper for adding message on the Notistack messages queue
 * @param msg message string which explains the feedback should be displayed
 */
export default {
    success(msg: string, opt?: ToastOptions) {
        this.toast({ msg, variant: 'success', ...opt });
    },
    warning(msg: string, opt?: ToastOptions) {
        this.toast({ msg, variant: 'warning', ...opt });
    },
    info(msg: string, opt?: ToastOptions) {
        const key = this.toast({ msg, variant: 'info', ...opt });
        return key;
    },
    error(msg: string, opt?: ToastOptions) {
        // The 60s read-model wait timeout is not a failure (the command is still being
        // processed): it is reported as a "please wait" warning so every call site showing the
        // raw error message gets the same treatment.
        const timeoutWarning = getTimeoutWarningForMessage(msg);
        if (timeoutWarning) {
            this.warning(timeoutWarning, opt);
            return;
        }
        // Error snackbars persist (manual close) by default; callers can opt out
        // with `{ persist: false }`.
        this.toast({ msg, variant: 'error', persist: true, ...opt });
    },
    toast({
        msg,
        variant = 'default',
        replaceKey,
        persist,
        action,
        preventDuplicate,
    }: {
        msg: string;
        variant: VariantType;
        replaceKey?: SnackbarKey;
        persist?: boolean;
        action?: SnackbarAction;
        preventDuplicate?: boolean;
    }): SnackbarKey {
        // The snackbar ref is only set once SnackbarUtilsConfigurator has mounted.
        // Guard against early calls (e.g. an Apollo error before the provider is
        // ready) so the toast helper can never throw and mask the original error.
        if (!useSnackbarRef) {
            // eslint-disable-next-line no-console
            console.error(`[SnackBarUtils] snackbar not ready, message dropped: ${msg}`);
            return '';
        }

        if (replaceKey !== undefined) {
            useSnackbarRef.closeSnackbar(replaceKey);
        }

        const dynamicAction: SnackbarAction = persist
            ? snackbarKey => (
                  <IconButton
                      onClick={() => useSnackbarRef.closeSnackbar(snackbarKey)}
                      color="primary"
                      invertedColor
                  >
                      <CloseIcon />
                  </IconButton>
              )
            : null;

        return useSnackbarRef.enqueueSnackbar(msg, {
            variant,
            preventDuplicate: preventDuplicate ?? true,
            persist,
            action: action ?? dynamicAction,
        });
    },
    close: (key?: SnackbarKey) => {
        if (!useSnackbarRef) {
            return;
        }
        useSnackbarRef.closeSnackbar(key);
    },
};
